// src/features/context/TenantConfigPanel.tsx
// Tenant-level Context Panel configuration: display limits plus a raw-JSON
// editor for per-entity-type section overrides (a nested map, edited as JSON
// rather than a bespoke form for every possible entity type).
// See docs/ux/context-ia.md section 3.

import { useEffect, useState } from 'react';
import { useI18n, useContext as useContextStore } from '@react/hooks';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { useScope } from '@/hooks/useScope';
import type { EntityTypeContextConfig } from '@core/types/context';
import { tryParseJson } from './logic';

export function TenantConfigPanel() {
  const { t } = useI18n();
  const store = useContextStore();
  const scope = useScope();
  const [maxInline, setMaxInline] = useState('3');
  const [maxExpanded, setMaxExpanded] = useState('10');
  const [maxCollapsed, setMaxCollapsed] = useState('20');
  const [inlineThreshold, setInlineThreshold] = useState('1');
  const [entityConfigsText, setEntityConfigsText] = useState('{}');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  const tenantEntity = { type_id: '', type_name: 'tenant', id: scope.tenantId, platform_id: scope.platformId, tenant_id: scope.tenantId, product_id: scope.productId };

  const load = async () => {
    setIsLoading(true);
    try {
      const r = await store.GetTenantContextConfig();
      const limits = r.config?.default_limits;
      if (limits) {
        setMaxInline(String(limits.max_inline));
        setMaxExpanded(String(limits.max_expanded));
        setMaxCollapsed(String(limits.max_collapsed));
        setInlineThreshold(String(limits.inline_threshold));
      }
      setEntityConfigsText(JSON.stringify(r.config?.entity_configs ?? {}, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const { value: entityConfigs, error } = tryParseJson<Record<string, EntityTypeContextConfig>>(entityConfigsText);
    if (error) {
      setJsonError(error);
      return;
    }
    setJsonError(null);
    setIsSaving(true);
    try {
      await store.UpdateTenantContextConfig({
        entity: tenantEntity,
        config: {
          tenant_id: scope.tenantId,
          entity_configs: entityConfigs,
          default_limits: {
            max_inline: parseInt(maxInline, 10) || 0,
            max_expanded: parseInt(maxExpanded, 10) || 0,
            max_collapsed: parseInt(maxCollapsed, 10) || 0,
            inline_threshold: parseInt(inlineThreshold, 10) || 0,
          },
        },
      });
      load();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <p className="text-sm font-medium text-foreground">{t('context.tenantConfig.limitsTitle')}</p>
      <div className="grid grid-cols-4 gap-3">
        <Input label={t('context.tenantConfig.maxInline')} type="number" min={0} value={maxInline} onChange={(e) => setMaxInline(e.target.value)} />
        <Input label={t('context.tenantConfig.maxExpanded')} type="number" min={0} value={maxExpanded} onChange={(e) => setMaxExpanded(e.target.value)} />
        <Input label={t('context.tenantConfig.maxCollapsed')} type="number" min={0} value={maxCollapsed} onChange={(e) => setMaxCollapsed(e.target.value)} />
        <Input label={t('context.tenantConfig.inlineThreshold')} type="number" min={0} value={inlineThreshold} onChange={(e) => setInlineThreshold(e.target.value)} />
      </div>
      <Textarea
        label={t('context.tenantConfig.entityConfigsJson')}
        value={entityConfigsText}
        onChange={(e) => setEntityConfigsText(e.target.value)}
        rows={10}
        className="font-mono text-xs"
        helperText={t('context.tenantConfig.entityConfigsHelp')}
      />
      {jsonError && <p className="text-sm text-destructive">{jsonError}</p>}
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : t('common.save')}</Button>
    </form>
  );
}
